"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type Card = {
  image: string;
  title: string;
  description: string;
  imageAlt?: string;
  titleColor?: string;
  descriptionColor?: string;
  gradientClassName?: string;
  blurBottom?: boolean;
};

type StackCarouselProps<TCard extends Card = Card> = {
  cards: TCard[];
  className?: string;
  viewportClassName?: string;
  slideClassName?: string;
  contentClassName?: string;
  showNavigation?: boolean;
  transitionMs?: number;
  autoScrollMs?: number;
  pauseAutoScroll?: boolean;
  loop?: boolean;
  /** Left inset of the first card as a % of the viewport container. Default 1.5; pass 0 to sit the deck flush against the container's left edge. */
  leftInsetPct?: number;
  onCardClick?: (card: TCard, index: number) => void;
  renderOverlay?: (card: TCard, index: number) => React.ReactNode;
};

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const getCircularDistance = (index: number, activeIndex: number, total: number) => {
  if (total <= 1) return 0;
  let diff = index - activeIndex;
  if (diff > total / 2) diff -= total;
  if (diff < -total / 2) diff += total;
  return diff;
};

type LayoutMode = "row" | "single";

/**
 * Card width / gap / stack peek (all % of viewport container width) and the
 * presentation `mode`:
 *  - `single`: small screens — exactly ONE card visible at a time. Next slides
 *    in from the right on top of the existing stack; Prev slides the top card
 *    off to the right.
 *  - `row`: tablet/desktop — multiple cards in a row with partial right peek;
 *    Next moves the leftmost card into the left stack.
 *
 * Row metrics are tuned so the next card naturally peeks at the right edge
 * whenever there is leftover horizontal space (~2 full + partial 3rd at lg/xl),
 * while staying close to the legacy `BenefitCard` footprint at each breakpoint.
 */
function getLayoutMetrics(visibleSlots: number) {
  if (visibleSlots <= 1) {
    return {
      cardWidthPct: 92,
      rowGapPct: 0,
      stackPeekPct: 0,
      mode: "single" as LayoutMode,
    };
  }
  if (visibleSlots === 2) {
    return {
      cardWidthPct: 56,
      rowGapPct: 2.8,
      stackPeekPct: 3,
      mode: "row" as LayoutMode,
    };
  }
  return {
    cardWidthPct: 42,
    rowGapPct: 2,
    stackPeekPct: 2.6,
    mode: "row" as LayoutMode,
  };
}

type CardLayout = {
  leftPct: number;
  widthPct: number;
  zIndex: number;
  scale: number;
  opacity: number;
  hidden: boolean;
  /** Extra translateX % (of self) for centered loop deck; 0 in normal row/stack mode. */
  loopSlideXPct: number;
};

function computeCardLayout(
  index: number,
  activeIndex: number,
  total: number,
  loop: boolean,
  metrics: {
    cardWidthPct: number;
    rowGapPct: number;
    stackPeekPct: number;
    mode: LayoutMode;
    leftInsetPct: number;
  }
): CardLayout {
  const { cardWidthPct, rowGapPct, stackPeekPct, mode, leftInsetPct } = metrics;
  const step = cardWidthPct + rowGapPct;
  // A card stays "visible" as long as ANY part of it is inside the viewport.
  // Hidden only when its left edge has crossed the right edge — viewport's
  // overflow:hidden then naturally clips the off-screen portion, giving a
  // partial peek when there's space.
  const rightClip = 100;
  const offscreenRightPct = 110;

  // --- Single-card mode (small screens): full-overlap stack ---
  // All cards share the same centered position; only z-index differs, so the
  // user sees just the active (top) card. Future cards are parked off-screen
  // right and slide in on Next; on Prev the top card slides back to the right.
  if (mode === "single" && !loop) {
    const k = activeIndex;
    const centerLeftPct = Math.max(0, (100 - cardWidthPct) / 2);

    if (index <= k) {
      return {
        leftPct: centerLeftPct,
        widthPct: cardWidthPct,
        zIndex: 40 + index,
        scale: 1,
        opacity: 1,
        hidden: false,
        loopSlideXPct: 0,
      };
    }

    return {
      leftPct: offscreenRightPct,
      widthPct: cardWidthPct,
      zIndex: 40 + index,
      scale: 1,
      opacity: 0,
      hidden: true,
      loopSlideXPct: 0,
    };
  }

  if (loop) {
    const distance = getCircularDistance(index, activeIndex, total);
    const absDistance = Math.abs(distance);
    const hidden = absDistance > 2;
    return {
      leftPct: 50 - cardWidthPct / 2,
      widthPct: cardWidthPct,
      zIndex: 100 - absDistance,
      scale: 1,
      opacity: hidden ? 0 : 1,
      hidden,
      loopSlideXPct: distance * 42,
    };
  }

  // --- Stack-on-left progression (one click = one card joins stack) ---
  //
  // activeIndex semantics:
  //   k = 0           -> normal row [0, 1, 2, ...] (no overlap visible)
  //   k = 1           -> cards [0, 1] stacked on left, [2 ...] in row
  //   k = total - 1   -> all cards stacked, row empty (Next becomes disabled)
  //
  // For N cards, this means exactly N-1 right-clicks fully stack the deck,
  // matching the user's: 3 cards -> 2 clicks to stack everything.

  const k = activeIndex;

  if (index <= k) {
    const leftPct = leftInsetPct + index * stackPeekPct;
    return {
      leftPct,
      widthPct: cardWidthPct,
      zIndex: 40 + index,
      scale: 1,
      opacity: 1,
      hidden: false,
      loopSlideXPct: 0,
    };
  }

  const stackEndPct =
    leftInsetPct + k * stackPeekPct + cardWidthPct + rowGapPct;
  const rel = index - k - 1;
  const leftPct = stackEndPct + rel * step;
  // Render as long as part of the card is in the viewport.
  const hidden = leftPct >= rightClip;

  return {
    leftPct,
    widthPct: cardWidthPct,
    zIndex: 80 + index,
    scale: 1,
    opacity: hidden ? 0 : 1,
    hidden,
    loopSlideXPct: 0,
  };
}

export default function StackCarousel<TCard extends Card = Card>({
  cards,
  className,
  viewportClassName,
  slideClassName,
  contentClassName,
  showNavigation = true,
  transitionMs = 420,
  autoScrollMs = 0,
  pauseAutoScroll = false,
  loop = false,
  leftInsetPct = 1.5,
  onCardClick,
  renderOverlay,
}: StackCarouselProps<TCard>) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleSlots, setVisibleSlots] = useState(4);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const transitionTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const updateVisibleSlots = () => {
      const width = window.innerWidth;
      if (width < MOBILE_BREAKPOINT) {
        setVisibleSlots(1);
        return;
      }
      if (width < TABLET_BREAKPOINT) {
        setVisibleSlots(2);
        return;
      }
      setVisibleSlots(4);
    };

    updateVisibleSlots();
    window.addEventListener("resize", updateVisibleSlots);
    return () => window.removeEventListener("resize", updateVisibleSlots);
  }, []);

  useEffect(() => {
    setActiveIndex((current) => {
      if (cards.length === 0) return 0;
      if (current <= cards.length - 1) return current;
      return cards.length - 1;
    });
  }, [cards.length]);

  useEffect(() => {
    if (cards.length <= 1 || autoScrollMs <= 0 || pauseAutoScroll || hasInteracted)
      return;
    const id = window.setInterval(() => {
      setActiveIndex((prev) => {
        if (loop) return (prev + 1) % cards.length;
        if (prev >= cards.length - 1) return prev;
        return prev + 1;
      });
    }, autoScrollMs);
    return () => window.clearInterval(id);
  }, [cards.length, autoScrollMs, pauseAutoScroll, loop, hasInteracted]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const layoutMetrics = useMemo(
    () => ({ ...getLayoutMetrics(visibleSlots), leftInsetPct }),
    [visibleSlots, leftInsetPct]
  );

  const lockTransition = useCallback(() => {
    if (transitionTimerRef.current) {
      window.clearTimeout(transitionTimerRef.current);
    }
    setIsTransitioning(true);
    transitionTimerRef.current = window.setTimeout(() => {
      setIsTransitioning(false);
      transitionTimerRef.current = null;
    }, Math.max(220, transitionMs - 40));
  }, [transitionMs]);

  const goPrev = useCallback(() => {
    if (cards.length <= 1 || isTransitioning) return;
    setHasInteracted(true);
    lockTransition();
    setActiveIndex((prev) =>
      loop ? (prev - 1 + cards.length) % cards.length : Math.max(0, prev - 1)
    );
  }, [cards.length, loop, isTransitioning, lockTransition]);

  const goNext = useCallback(() => {
    if (cards.length <= 1 || isTransitioning) return;
    setHasInteracted(true);
    lockTransition();
    setActiveIndex((prev) =>
      loop ? (prev + 1) % cards.length : Math.min(cards.length - 1, prev + 1)
    );
  }, [cards.length, loop, isTransitioning, lockTransition]);

  if (!cards?.length) return null;

  return (
    <section className={cn("w-full", className)}>
      <div
        className={cn(
          "relative mx-auto w-full overflow-visible",
          /*
           * Fallback height ONLY when the caller supplies none.
           *
           * It used to be unconditional, which meant two sets of `h-*` on one
           * element — and which set wins is decided by the order Tailwind emits
           * them, not by the order they are passed to `cn`. This project
           * redefines the named breakpoints (sm = 1080px, lg = 1430px), so
           * `sm:h-[340px]` / `lg:h-[400px]` were emitted AFTER a caller's
           * `min-[640px]:h-[420px]` / `min-[1024px]:h-[500px]` and silently beat
           * them. ProgramBenefits2 passes exactly that override, with a comment
           * saying it is there so the caption offsets below line up — and it was
           * being discarded, leaving the caption hanging 62px past the bottom of
           * a card that clips its overflow.
           *
           * See .claude/rules/tailwind-conflicts.md.
           */
          !viewportClassName && "h-[280px] sm:h-[340px] lg:h-[400px]",
          viewportClassName
        )}
      >
        {cards.map((card, index) => {
          const layout = computeCardLayout(
            index,
            activeIndex,
            cards.length,
            loop,
            layoutMetrics
          );

          return (
            <article
              key={`${card.title}-${index}`}
              className={cn("absolute top-0 h-full", slideClassName)}
              style={{
                left: `${layout.leftPct}%`,
                width: `${layout.widthPct}%`,
                zIndex: layout.zIndex,
                transform:
                  layout.loopSlideXPct !== 0
                    ? `translateX(${layout.loopSlideXPct}%) scale(${layout.scale})`
                    : `scale(${layout.scale})`,
                transformOrigin:
                  layout.loopSlideXPct !== 0 ? "center center" : "top left",
                opacity: layout.opacity,
                transition: `left ${transitionMs}ms ease, transform ${transitionMs}ms ease, opacity ${transitionMs}ms ease`,
                willChange: "left, transform, opacity",
                pointerEvents: layout.hidden ? "none" : "auto",
              }}
              aria-hidden={layout.hidden}
            >
              <div
                className={cn(
                  "relative h-full w-full overflow-clip flex flex-col justify-end border-l-[2.5px] border-l-white/40",
                  "rounded-[16px] sm:rounded-[40px]",
                  contentClassName
                )}
                onClick={() => onCardClick?.(card, index)}
              >
                {/*
                  next/image, not a raw <img>: a raw tag ships the CMS original
                  untouched — no resize, no modern format. PSI measured five cards
                  at ~180 KiB each arriving at 1308x1086 for a 560x420 slot, 1,043 KiB
                  of waste on desktop alone.

                  `sizes` mirrors getLayoutMetrics(): the card is 92% of the container
                  below 768px (1 slot), 56% below 1024px (2 slots), and 42% above
                  (4 slots). 50vw on desktop leaves headroom for the widened active
                  card rather than under-serving and upscaling it.

                  `fill` supplies its own absolute inset-0 / 100% sizing, so only
                  object-cover is left on the class list.
                */}
                {card.image ? (
                  <Image
                    src={card.image}
                    alt={card.imageAlt || card.title}
                    fill
                    sizes="(max-width: 767px) 92vw, (max-width: 1023px) 56vw, 50vw"
                    className="object-cover"
                    draggable={false}
                  />
                ) : null}
                <div
                  className={cn(
                    "pointer-events-none absolute inset-x-0 bottom-0 h-[58%]",
                    card.gradientClassName ||
                      "bg-linear-to-t from-black/80 via-black/40 to-transparent"
                  )}
                />
                {/* Blur layer removed for stack carousel; only gradient stays for clean text readability. */}
                {renderOverlay?.(card, index)}
                <div
                  className={cn(
                    "absolute left-1/2 -translate-x-1/2 flex flex-col z-10 text-white",
                    /*
                     * Spelled out, NOT `sm:` / `lg:` / `xl:`.
                     *
                     * These offsets are copied from ProgramBenefits2's own card,
                     * where they are authored against card heights that change at
                     * 640px and 1024px. The named variants in this project are
                     * 1080 / 1430 / 1750, so the text changed position at
                     * completely different widths from the card it sits in — 140px
                     * down a 500px card at 1024, then 376px down a 340px card at
                     * 1080. Same rule the lead-form skins follow: do not tidy
                     * these to named variants.
                     */
                    "top-[140px] w-[88%] gap-[4px]",
                    "min-[640px]:top-[317px] min-[640px]:gap-[8px]",
                    "min-[1024px]:top-[376px] min-[1024px]:gap-[8px]",
                    "min-[1280px]:top-[376px] min-[1280px]:gap-[8px]"
                  )}
                >
                  <p
                    className={cn(
                      "font-semibold leading-[1.4] w-full line-clamp-2",
                      "text-[14px] tracking-[0.14px]",
                      "min-[640px]:text-[20px] min-[640px]:tracking-[0.2px]",
                      "min-[1024px]:text-[24px] min-[1024px]:tracking-[0.24px]",
                      "min-[1280px]:text-[24px] min-[1280px]:tracking-[0.24px]"
                    )}
                    style={{
                      fontFamily: "var(--font-wf-visual-sans)",
                      color: card.titleColor || "#FFFFFF",
                    }}
                  >
                    {card.title}
                  </p>
                  <p
                    className={cn(
                      "font-medium leading-[1.49] w-full line-clamp-3 min-[640px]:line-clamp-3",
                      "text-[9px]",
                      "min-[640px]:text-[12px]",
                      "min-[1024px]:text-[14px] min-[1280px]:text-[14px]"
                    )}
                    style={{
                      fontFamily: "var(--font-wf-visual-sans)",
                      color: card.descriptionColor || "#FFFFFF",
                    }}
                  >
                    {card.description}
                  </p>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {showNavigation ? (
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={goPrev}
            className={cn(
              "grid size-10 place-items-center rounded-full",
              "bg-white/90 text-zinc-900 shadow-md",
              "transition-all duration-300 hover:scale-105 hover:bg-white",
              "disabled:cursor-not-allowed disabled:opacity-40"
            )}
            aria-label="Previous card"
            disabled={
              cards.length <= 1 || isTransitioning || (!loop && activeIndex === 0)
            }
          >
            <span className="text-lg leading-none">{"\u2039"}</span>
          </button>

          <button
            type="button"
            onClick={goNext}
            className={cn(
              "grid size-10 place-items-center rounded-full",
              "bg-white text-zinc-900 shadow-md",
              "transition-all duration-300 hover:scale-105 hover:bg-white",
              "disabled:cursor-not-allowed disabled:opacity-40"
            )}
            aria-label="Next card"
            disabled={
              cards.length <= 1 ||
              isTransitioning ||
              (!loop && activeIndex === cards.length - 1)
            }
          >
            <span className="text-lg leading-none">{"\u203A"}</span>
          </button>
        </div>
      ) : null}
    </section>
  );
}
